import { Cookie } from '@repo/tools'
import { Api } from '../base-api/Api'
export * from '../base-api/Api'

const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL

export const baseApi = new Api({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
})

baseApi.instance.interceptors.request.use((config) => {
  if (
    config.url &&
    (config.url.includes('/user/refresh') ||
      config.url.includes('/agent/refresh'))
  ) {
    return config
  }

  // Exclude OTP endpoints from adding Authorization header
  if (
    config.url &&
    (config.url.includes('/otp') ||
      config.url.includes('/login') ||
      config.url.includes('/register') ||
      config.url.includes('/check'))
  ) {
    return config
  }

  const accessToken = Cookie.get('access_token')

  if (accessToken) {
    config.headers.Authorization = accessToken
  }

  if (config.headers?.authorization === null) {
    config.headers.Authorization = undefined
  }

  return config
})

baseApi.instance.interceptors.response.use(
  (response) => {
    const url = response?.config?.url

    // Check for tokens in response headers (cookies)
    const newAccessToken = response.headers?.['new-access-token']
    const newRefreshToken = response.headers?.['new-refresh-token']

    if (newAccessToken || newRefreshToken) {
      // Use tokens from headers if available
      const accessToken = newAccessToken || Cookie.get('access_token')
      const refreshToken = newRefreshToken || Cookie.get('refresh_token')
      Cookie.setAuthTokens(accessToken, refreshToken)
    } else if (
      url &&
      [
        '/user/phone/login',
        '/user/password/login',
        '/agent/password/login',
        '/agent/phone/login',
        '/user/email/login',
        '/agent/email/login',
        '/user/phone/register',
        '/agent/phone/register',
        '/user/email/register',
        '/agent/email/register',
      ].some((endpoint) => url.includes(endpoint)) &&
      response.data?.accessToken &&
      response.data?.refreshToken
    ) {
      // Fallback to response data if headers don't contain tokens
      Cookie.setAuthTokens(
        response.data.accessToken,
        response.data.refreshToken,
      )
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !(
        originalRequest.url &&
        (originalRequest.url.includes('/user/refresh') ||
          originalRequest.url.includes('/agent/refresh'))
      )
    ) {
      originalRequest._retry = true

      const refreshToken = Cookie.get('refresh_token')

      if (refreshToken) {
        try {
          const _api = originalRequest.url?.includes('/agent')
            ? baseApi.v1.agentAuthRefresh
            : baseApi.v1.userAuthRefresh
          const response = await _api(
            { refreshToken },
            {
              headers: {
                Authorization: undefined,
              },
            },
          )

          // Check for tokens in response headers first
          const headerAccessToken = response.headers?.['new-access-token']
          const headerRefreshToken = response.headers?.['new-refresh-token']

          const newAccessToken = headerAccessToken || response.data.accessToken
          const newRefreshToken =
            headerRefreshToken || (response.data.refreshToken ?? '')

          if (newAccessToken) {
            Cookie.setAuthTokens(newAccessToken, newRefreshToken)
            originalRequest.headers.Authorization = newAccessToken
            return baseApi.instance(originalRequest)
          }
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError)
          // If refresh token also fails with 401, redirect to login
          const _status = (refreshError as { response: { status: number } })
            ?.response?.status
          if (
            (_status === 401 || _status === 409) &&
            typeof window !== 'undefined'
          ) {
            Cookie.removeAuthTokens()
            // Save current URL before redirecting to login
            Cookie.setRedirectingUrl(window.location.pathname)
            window.location.href = '/auth/login'
          }
        }
      } else if (typeof window !== 'undefined') {
        Cookie.removeAuthTokens()
        // Save current URL before redirecting to login
        Cookie.setRedirectingUrl(window.location.pathname)
        window.location.href = '/auth/login'
      }
    }

    return Promise.reject(error)
  },
)

export const api = {
  v1: {
    ...baseApi.v1,
  },
}

// TODO: Remove this once the API is updated
export interface CheckUserGetParams {
  phone?: string
  email?: string
}
